/**
 * Properties-table aspect (BPT) — Phase 5.1 PR 5.1.c.
 *
 * `ACDBBLOCKPROPERTIESTABLE` records carry the lookup-table-driven
 * column/row spreadsheet that DYN-4 Phase 4a Fix 5 introduced. This
 * aspect is **write-only** at registration time:
 *
 *   - Parse path: the BPT decoder lives inside
 *     `decodeConstraintPropertiesTable` in
 *     `dxfBridge.dynamic.parser.tier2.ts`, which is already invoked
 *     by the legacy dispatcher's tier-2 branch. Carving a symmetric
 *     standalone `parseBpt` function out of tier-2 is out of scope
 *     for PR 5.1.c — it would require splitting tier-2 itself, which
 *     PR 5.1.f considers separately.
 *
 *   - Write path: `tryWriteConstraintBlockPropertiesTable` already
 *     exists as a clean standalone function in
 *     `dxfBridge.dynamic.writer.bpt.ts`. Wrapping it in a
 *     `DynamicAspect` gives the registry the entry it needs to route
 *     emission once the dispatcher is rewritten in PR 5.1.d.
 *
 * Therefore `BPT_ASPECT.parse` is undefined; the registry interface
 * makes `parse` optional precisely for this case.
 */

import type { BlockPropertiesTable } from "./dynamicBlock";
import type { DynamicAspect } from "./dxfBridge.dynamic.aspect";
import { registerAspect } from "./dxfBridge.dynamic.aspect";
import {
  tryWriteConstraintBlockPropertiesTable,
  type BptWriterContext,
} from "./dxfBridge.dynamic.writer.bpt";

/**
 * Canonical class string recognised by this aspect. There is only one
 * (the ACDB-prefixed AutoCAD 2018+ form). Pre-2018 un-prefixed
 * `BLOCKPROPERTIESTABLE` is canonicalised to the prefixed form before
 * dispatch (see `canonicalDynamicBlockClass` in
 * `dxfBridge.dynamic.shared.ts`).
 */
export const PROPERTIES_TABLE_CLASSES: ReadonlySet<string> = new Set([
  "ACDBBLOCKPROPERTIESTABLE",
]);

export const PROPERTIES_TABLE_ASPECT: DynamicAspect<
  BlockPropertiesTable,
  void,
  BptWriterContext
> = {
  id: "properties-table",
  classes: PROPERTIES_TABLE_CLASSES,
  matches(canonicalType) {
    return PROPERTIES_TABLE_CLASSES.has(canonicalType);
  },
  // No parse — see header comment. Dispatcher falls through to the
  // legacy tier-2 inline decoder for BPT records.
  write(table, ctx) {
    return tryWriteConstraintBlockPropertiesTable(table, ctx);
  },
};

registerAspect(PROPERTIES_TABLE_ASPECT);
